// Kompas Dnia — legal content module.
// Structured content for the four /legal/* pages (Privacy, Terms, Delete data, AI Notice).
// Kept outside src/i18n/translations.js on purpose: it's long, richly structured, and it
// mixes headings with paragraphs, so a dedicated module is more readable than deep i18n.

export const LEGAL_META = {
  publisher: "Pewnosc Siebie AI",
  contactEmail: "contact@kompasdnia.app",
  effectiveDateISO: "2026-07-01",
  effectiveDate: {
    pl: "1 lipca 2026",
    en: "July 1, 2026",
  },
  version: "0.1.0 MVP",
  footer: {
    pl: "Wydawca: Pewnosc Siebie AI · Data wejścia w życie: 1 lipca 2026 · Kontakt: contact@kompasdnia.app",
    en: "Publisher: Pewnosc Siebie AI · Effective date: July 1, 2026 · Contact: contact@kompasdnia.app",
  },
  effectiveLabel: {
    pl: "Data wejścia w życie: 1 lipca 2026",
    en: "Effective date: July 1, 2026",
  },
};

export const LEGAL_CONTENT = {
  privacy: {
    title: { pl: "Polityka prywatności", en: "Privacy Policy" },
    sections: [
      {
        heading: { pl: "1. O aplikacji", en: "1. About the app" },
        body: {
          pl: "Kompas Dnia (Daily Compass AI) to aplikacja do codziennej refleksji, która pomaga wybrać kierunek dnia. Aplikacja nie jest narzędziem medycznym, terapeutycznym ani diagnostycznym.",
          en: "Kompas Dnia (Daily Compass AI) is a daily reflection app that helps you choose a direction for your day. It is not a medical, therapeutic or diagnostic tool.",
        },
      },
      {
        heading: { pl: "2. Jakie dane zbieramy", en: "2. What data we collect" },
        body: {
          pl: "W wersji MVP aplikacja nie tworzy kont użytkownika i nie wymaga logowania. Nie zbieramy Twoich danych osobowych — nie potrzebujemy Twojego imienia, adresu email, numeru telefonu ani żadnych innych danych identyfikujących.",
          en: "In the MVP version, the app does not create user accounts and does not require login. We do not collect your personal data — we do not need your name, email, phone number or any other identifying information.",
        },
      },
      {
        heading: {
          pl: "3. Gdzie przechowywane są dane check-inów",
          en: "3. Where check-in data is stored",
        },
        body: {
          pl: "Wszystkie odpowiedzi z check-inów, refleksje oraz ustawienia (język, stan onboardingu) są zapisywane wyłącznie lokalnie na Twoim urządzeniu za pomocą mechanizmu localStorage w Twojej przeglądarce lub aplikacji PWA/TWA. Dane te nie są wysyłane na żaden serwer i nie opuszczają Twojego urządzenia.",
          en: "All check-in answers, reflections and settings (language, onboarding state) are stored exclusively locally on your device using the localStorage mechanism in your browser or PWA/TWA app. This data is not sent to any server and does not leave your device.",
        },
      },
      {
        heading: { pl: "4. Kontrola nad danymi", en: "4. Control over your data" },
        body: {
          pl: 'Możesz w każdej chwili usunąć wszystkie lokalne dane aplikacji, klikając przycisk "Usuń dane lokalne" w ekranie Ustawienia. Po tej operacji aplikacja wróci do stanu początkowego (onboarding, domyślny język PL). Możesz również wyczyścić dane przeglądarki dla domeny aplikacji.',
          en: 'You can delete all local app data at any time by tapping "Delete local data" on the Settings screen. After this operation the app returns to its initial state (onboarding, default Polish language). You can also clear browser data for the app domain.',
        },
      },
      {
        heading: { pl: "5. Pliki cookie i śledzenie", en: "5. Cookies and tracking" },
        body: {
          pl: "Sama aplikacja nie używa plików cookie do śledzenia ani nie stosuje analityki firm trzecich w wersji MVP oraz nie sprzedaje żadnych danych.",
          en: "The app itself does not use tracking cookies, does not use third-party analytics in the MVP version and does not sell any data.",
        },
      },
      {
        heading: { pl: "6. Sztuczna inteligencja", en: "6. Artificial Intelligence" },
        body: {
          pl: "Rekomendacje generowane w aplikacji pochodzą z lokalnej logiki i szablonów tekstowych działających w Twojej przeglądarce. W wersji MVP aplikacja nie wysyła Twoich odpowiedzi do zewnętrznych modeli AI.",
          en: "Recommendations generated in the app come from local logic and text templates running in your browser. In the MVP, the app does not send your answers to any external AI models.",
        },
      },
      {
        heading: { pl: "7. RODO/GDPR", en: "7. GDPR" },
        body: {
          pl: "Ponieważ aplikacja nie przetwarza Twoich danych osobowych poza Twoim urządzeniem, standardowe wymogi RODO dotyczące przetwarzania danych nie mają praktycznego zastosowania. Zachowujesz pełną kontrolę nad swoimi danymi — są one wyłącznie w Twoich rękach. Zgodnie z RODO masz prawo do wglądu, sprostowania i usunięcia swoich danych — w tej aplikacji realizujesz to samodzielnie w ustawieniach.",
          en: "Because the app does not process your personal data outside your device, standard GDPR data-processing requirements have no practical application. You retain full control of your data — it lives only in your hands. Under GDPR you have the right to access, correct and delete your data — in this app you exercise these rights directly through the settings.",
        },
      },
      {
        heading: { pl: "8. Bezpieczeństwo dzieci", en: "8. Children's safety" },
        body: {
          pl: "Aplikacja nie jest skierowana do osób poniżej 13 roku życia i nie zbiera świadomie danych osób nieletnich.",
          en: "The app is not directed at persons under 13 and does not knowingly collect data from minors.",
        },
      },
      {
        heading: { pl: "9. Zmiany polityki", en: "9. Policy changes" },
        body: {
          pl: "Możemy aktualizować tę Politykę Prywatności. O istotnych zmianach poinformujemy w aplikacji.",
          en: "We may update this Privacy Policy. We will inform you of material changes within the app.",
        },
      },
      {
        heading: { pl: "10. Kontakt", en: "10. Contact" },
        body: {
          pl: "W sprawach dotyczących prywatności napisz: contact@kompasdnia.app",
          en: "For privacy questions write to: contact@kompasdnia.app",
        },
      },
    ],
  },

  terms: {
    title: { pl: "Regulamin", en: "Terms of Use" },
    sections: [
      {
        heading: { pl: "1. Charakter aplikacji", en: "1. Nature of the app" },
        body: {
          pl: "Kompas Dnia to narzędzie informacyjne i refleksyjne. Pomaga w codziennej organizacji myśli i wyborze kierunku dnia. W wersji MVP aplikacja działa w trybie gościa — nie tworzy konta użytkownika i nie wymaga logowania. Aplikacja nie jest i nie zastępuje: pomocy medycznej, psychologicznej, terapeutycznej, prawnej, finansowej ani żadnego innego doradztwa zawodowego.",
          en: "Kompas Dnia is an informational and reflective tool. It helps you organize your thoughts and choose a direction for your day. In the MVP the app runs in guest mode — it does not create user accounts and does not require login. The app is not, and does not replace: medical, psychological, therapeutic, legal, financial or any other professional advice.",
        },
      },
      {
        heading: { pl: "2. Odpowiedzialność użytkownika", en: "2. User responsibility" },
        body: {
          pl: "Korzystasz z aplikacji na własną odpowiedzialność. Rekomendacje generowane przez aplikację mają charakter ogólny i pomocniczy. To Ty sam decydujesz, czy i jak zastosować sugestię w swojej sytuacji.",
          en: "You use the app at your own risk. Recommendations generated by the app are general and supportive in nature. You alone decide whether and how to apply a suggestion in your own situation.",
        },
      },
      {
        heading: { pl: "3. Brak diagnozy", en: "3. No diagnosis" },
        body: {
          pl: "Aplikacja nie stawia diagnoz — nie ocenia stanu zdrowia fizycznego ani psychicznego. Jeśli doświadczasz trudności zdrowotnych, skontaktuj się z odpowiednim specjalistą.",
          en: "The app does not diagnose — it does not evaluate your physical or mental health. If you are experiencing health difficulties, please contact an appropriate professional.",
        },
      },
      {
        heading: {
          pl: "4. Ograniczenie odpowiedzialności",
          en: "4. Limitation of liability",
        },
        body: {
          pl: "W maksymalnym zakresie dozwolonym prawem, wydawca aplikacji nie odpowiada za skutki decyzji podjętych przez użytkownika na podstawie treści z aplikacji.",
          en: "To the maximum extent permitted by law, the app publisher is not liable for the outcomes of decisions made by users based on content from the app.",
        },
      },
      {
        heading: { pl: "5. Własność intelektualna", en: "5. Intellectual property" },
        body: {
          pl: "Nazwa aplikacji, logotyp, treści i kod źródłowy są własnością wydawcy albo są używane na podstawie odpowiednich licencji.",
          en: "The app name, logo, content and source code are owned by the publisher or used under appropriate licences.",
        },
      },
      {
        heading: { pl: "6. Zmiany regulaminu", en: "6. Changes to these terms" },
        body: {
          pl: "Wydawca zastrzega sobie prawo do zmian regulaminu. O istotnych zmianach poinformujemy w aplikacji.",
          en: "The publisher reserves the right to change these terms. We will inform you of material changes within the app.",
        },
      },
      {
        heading: { pl: "7. Prawo właściwe", en: "7. Governing law" },
        body: {
          pl: "Do niniejszego regulaminu stosuje się prawo polskie. Wszelkie spory rozstrzygane będą przez właściwe sądy w Polsce.",
          en: "This agreement is governed by Polish law. Any disputes will be resolved by the competent courts in Poland.",
        },
      },
      {
        heading: { pl: "8. Kontakt", en: "8. Contact" },
        body: {
          pl: "W sprawach regulaminowych napisz: contact@kompasdnia.app",
          en: "For questions about these terms write to: contact@kompasdnia.app",
        },
      },
    ],
  },

  deleteData: {
    title: {
      pl: "Usuwanie danych i konta",
      en: "Deleting your data and account",
    },
    sections: [
      {
        heading: {
          pl: "1. Tryb gościa — brak konta",
          en: "1. Guest mode — no account created",
        },
        body: {
          pl: "Aplikacja Kompas Dnia w wersji MVP działa w trybie gościa. Nie tworzymy konta użytkownika, nie wymagamy logowania i nie przechowujemy Twoich danych na naszych serwerach. Nie ma więc konta, które można by usunąć.",
          en: "Kompas Dnia (Daily Compass AI) in MVP works in guest mode. We do not create user accounts, do not require login and do not store your data on our servers. There is no account to delete.",
        },
      },
      {
        heading: {
          pl: "2. Usuwanie danych lokalnych",
          en: "2. Deleting local data",
        },
        body: {
          pl: 'Wszystkie Twoje dane (check-iny, refleksje, ustawienia) są zapisane wyłącznie na Twoim urządzeniu. Aby je usunąć: 1) Otwórz aplikację, 2) Przejdź do Ustawienia, 3) Kliknij „Usuń dane lokalne", 4) Potwierdź operację. Alternatywnie: wyczyść dane przeglądarki dla domeny aplikacji lub odinstaluj aplikację PWA/TWA.',
          en: 'All your data (check-ins, reflections, settings) is stored only on your device. To delete it: 1) Open the app, 2) Go to Settings, 3) Tap "Delete local data", 4) Confirm. Alternatively: clear browser data for the app domain or uninstall the PWA/TWA app.',
        },
      },
      {
        heading: {
          pl: "3. Kontakt w sprawie danych",
          en: "3. Contact regarding data",
        },
        body: {
          pl: "Jeśli w przyszłości aplikacja przejdzie na model z kontami użytkowników i backendem, procedura usuwania konta zostanie tutaj rozszerzona zgodnie z wymogami Google Play i RODO. Do tego czasu, jeśli masz pytania dotyczące danych, skontaktuj się: contact@kompasdnia.app",
          en: "If the app introduces user accounts and a backend in the future, this page will be updated with the account deletion procedure, in line with Google Play and GDPR requirements. Until then, if you have data questions: contact@kompasdnia.app",
        },
      },
      {
        heading: {
          pl: "4. Bez płatnej subskrypcji",
          en: "4. No paid subscription",
        },
        body: {
          pl: 'W MVP nie ma płatnej subskrypcji. Premium jest tylko zapowiedzią przyszłych funkcji („wkrótce"). Nie musisz nic anulować ani rezygnować z żadnego planu.',
          en: 'The MVP has no paid subscription. Premium is only announced as an upcoming feature ("coming soon"). You do not need to cancel or opt out of any plan.',
        },
      },
    ],
  },

  aiNotice: {
    title: { pl: "Informacja o AI", en: "AI Notice" },
    sections: [
      {
        heading: { pl: "1. Rola AI w aplikacji", en: "1. The role of AI in the app" },
        body: {
          pl: "Kompas Dnia korzysta z prostej lokalnej logiki i szablonów tekstowych, aby generować krótkie rekomendacje i pytania refleksyjne na podstawie Twoich odpowiedzi z check-inu.",
          en: "Kompas Dnia uses simple local logic and text templates to generate short recommendations and reflective prompts based on your check-in answers.",
        },
      },
      {
        heading: { pl: "2. Co to oznacza w MVP", en: "2. What this means in the MVP" },
        body: {
          pl: "W wersji MVP aplikacja nie łączy się z żadnym zewnętrznym modelem AI, nie wysyła Twoich odpowiedzi na serwery firm trzecich i nie używa Twoich danych do trenowania modeli.",
          en: "In the MVP, the app does not connect to any external AI model, does not send your answers to any third-party server and does not use your data to train models.",
        },
      },
      {
        heading: { pl: "3. Charakter rekomendacji", en: "3. Nature of the recommendations" },
        body: {
          pl: "Rekomendacje mają charakter pomocniczy i refleksyjny. Nie są to porady medyczne, psychologiczne, prawne, finansowe ani zawodowe. Zawsze samodzielnie oceń, czy sugestia pasuje do Twojej sytuacji i potrzeb.",
          en: "The recommendations are supportive and reflective. They are not medical, psychological, legal, financial or professional advice. Always assess for yourself whether a suggestion fits your situation and needs.",
        },
      },
      {
        heading: { pl: "4. Bez decyzji za Ciebie", en: "4. No decisions on your behalf" },
        body: {
          pl: "Aplikacja nie podejmuje decyzji za Ciebie. Pokazuje kierunek, ale to Ty decydujesz, co z nim zrobisz.",
          en: "The app does not make decisions for you. It points a direction, but you decide what to do with it.",
        },
      },
      {
        heading: { pl: "5. Przyszłe wersje", en: "5. Future versions" },
        body: {
          pl: "Jeśli w przyszłości podłączymy zewnętrzne modele AI (np. dla głębszych refleksji w wersji Premium), poinformujemy o tym wyraźnie w tej sekcji, w polityce prywatności i wewnątrz aplikacji, oraz umożliwimy Ci opt-in/opt-out.",
          en: "If we connect external AI models in the future (e.g. for deeper reflections in Premium), we will inform you clearly in this section, in the Privacy Policy and inside the app, and we will let you opt in or out.",
        },
      },
      {
        heading: { pl: "6. Kontakt", en: "6. Contact" },
        body: {
          pl: "Pytania o AI: contact@kompasdnia.app",
          en: "AI questions: contact@kompasdnia.app",
        },
      },
    ],
  },
};
